import { z } from "zod";
import { CarTireSetModel } from "../../../car/domain/models/carTireSetModel";
import {
  CarTireModel,
  CarTireResponseModel,
} from "../../../car/domain/models/carTireModel";
import { CarResponseModel } from "../../../car/domain/models/carModel";
import { PackingSlipModel } from "../../../planning/dailyPlanning/domain/models/packingSlipModel";

export const requestSchema = z.object({
  id: z.number(),
  source_type: z.string(),
  source_id: z.number(),
  type: z.enum(["pickup", "deliver"]),
  relation_id: z.string(),
  updated_at: z.date().optional(),
  user_id: z.number(),
  invoice_status: z.enum(["to_invoice", "invoiced", "no_invoice"]).optional(),
  work_order_number: z.string().optional().nullable(),
  comment: z.string().optional().nullable(),
  is_from_dbb_api: z.boolean().optional().nullable(),
  in_out_storage: z.date().optional().nullable(),
  pickup_and_delivery_date: z.date(),
  freestock: z.boolean(),
  stateFlow: z.object({
    completed: z.string(),
    cancelled: z.string(),
    value: z.string(),
    type: z.string(),
  }),
  availableTransitions: z.array(z.string()),

  tiresFreestock: z.number().array().optional().nullable(),
  requestTires: z.object({
    id: z.number(),
    tire: z.object({
      id: z.number(),
      label: z.string(),
      status: z.boolean(),
    }),
    upsert: z
      .object({
        id: z.number().optional(),
        tire_id: z.number(),
      })
      .array(),
    delete: z.number().array(),
  }),

  source: z.object({
    "... on Tire": z.object({
      id: z.string(),
      brand: z.string(),
    }),
    "... on Tireset": z.object({
      id: z.string(),
      type: z.string(),
      car: z.object({
        id: z.string(),
        licence: z.string(),
        additional_info: z.string(),
      }),
      position: z.object({
        id: z.string(),
        label: z.string(),
      }),
      tires: z.object({
        id: z.number(),
        label: z.string().optional().nullable(),
        brand: z.string().optional().nullable(),
        brand_type: z.string().optional().nullable(),
        type: z.string().optional().nullable(),
        ean: z.string().optional().nullable(),
        profile_depth: z.string().optional().nullable(),
        size: z.string().optional().nullable(),
        run_flat: z.string().optional().nullable(),
        car_position: z.string().optional().nullable(),
        status: z.boolean(),
      }),
    }),
  }),

  //fake
  licence_plate: z.string().optional(),
  tire_type: z.enum(["winter", "summer", "all_season"]).optional(),

  packingSlip: z.object({
    id: z.number(),
    date: z.date(),
  }),
});

export interface requestMutationModel {
  id: number | undefined;
  type: string;
  state?: string;
  pickupAndDeliveryDate?: string | undefined;
  inOutStorage?: string | undefined;

  relation_id?: number;

  sourceType: string | undefined;
  sourceId: number | undefined;

  invoiceStatus?: string;
  invoiced?: boolean;

  workOrderNumber?: string;

  createdAt?: string;
  active?: boolean;

  tiresFreestock?: number[];

  requestTires: {
    upsert?: {
      id?: number;
      tire_id: number;
    }[];
    delete?: number[];
  };

  source?: CarTireSetModel | CarTireModel | undefined;
}

export interface RequestModel {
  id: number | undefined;
  type: string;
  state?: string;
  pickupAndDeliveryDate?: string | undefined;
  inOutStorage?: string | undefined;

  sourceType: string | undefined;
  sourceId: number | undefined;

  invoiceStatus?: string;
  invoiced?: boolean;

  workOrderNumber?: string;

  createdAt?: string;
  active?: boolean;

  availableTransitions?: string[];

  stateFlow: {
    completed: boolean;
    refused?: boolean;
    cancelled?: boolean;
    value: string;
    type: string;
  }[];

  relationId?: number;
  relation?: {
    id: number;
    name?: string;
  };
  userId?: number;

  shippingListId?: number;

  requestTires?: requestTiresResponseModel[];

  source?: CarTireSetModel | CarTireModel | undefined;

  finalized?: boolean;
}

export interface RequestResponseModel {
  id?: number | undefined;
  type: string;
  state: string;
  pickup_and_delivery_date?: string | undefined;
  in_out_storage?: string | undefined;

  source_type: string | undefined;
  source_id: number | undefined;

  invoice_status?: string;
  invoiced: boolean;

  freestock: boolean;

  work_order_number: string;

  comment?: string | null;

  is_from_dbb_api?: boolean;

  created_at: string;
  updated_at: string;
  active: boolean;
  status_updated: string;

  stateFlow: {
    completed: boolean;
    refused?: boolean;
    cancelled?: boolean;
    value: string;
    type: string;
  }[];

  availableTransitions: string[];

  relation?: {
    id: number;
    name?: string;
  };
  user?: {
    id: number;
  };

  shipping_list_id?: {
    id: number;
  };

  source: {
    __typename: string;
    id: number;
    type?: string;
    sleep_set?: boolean;
    in_hotel?: boolean;
    tires?: CarTireResponseModel[];
    car?: CarResponseModel;
    sticker?: {
      pdfUrl: string;
    };
  };

  packingSlip?: PackingSlipModel | undefined;

  requestTires?: requestTiresResponseModel[];
}

export interface requestTiresResponseModel {
  id: number;
  tire: CarTireResponseModel;
}

export interface AllRequestQueryResponseModel {
  pickupStates: string[];
  deliverStates: string[];
}
